import Vue from "vue";
import LazyLoad from "vanilla-lazyload/dist/lazyload";
import "fg-loadcss/dist/cssrelpreload";
import objectFitImages from "object-fit-images";
import { utils, KeyEventCodes, isMobile } from "~/Scripts/mixins/common";
import MiniBasket from "~/Components/Basket/MiniBasket.vue";
import Btn from "~/Components/Base/TheDayButton.vue";

/**
 * A mixin that we're going to include in every VueApp
 * instance. Used to provide common functionality to all pages.
 */
export default Vue.extend( {
  components: {
    Btn,
    MiniBasket,
  },
  mixins: [utils, KeyEventCodes, isMobile],
  data: () => ( {
    showOverlay: false,
    showMegaMenu: false,
    preventHidingOverlay: false,
    preventHidingMegaMenu: false,
    menuItemsState: {},
    timeouts: [],
  } ),
  watch: {
    showOverlay ( val ) {
      if ( val ) {
        this.preventHidingOverlay = true;
      }
    },
    preventHidingOverlay () {
      window.setTimeout( () => {
        this.preventHidingOverlay = false;
      }, 300 );
    },
    showMegaMenu ( val ) {
      if ( val ) {
        this.preventHidingMegaMenu = true;
      }
    },
    preventHidingMegaMenu () {
      window.setTimeout( () => {
        this.preventHidingMegaMenu = false;
      }, 300 );
    },
  },
  mounted () {
    new LazyLoad( {
      elements_selector: "img, .lazy",
    } );

    objectFitImages();
  },
  methods: {
    mouseEnterMenuItem ( alias ) {
      this.timeouts.push( window.setTimeout( () => {
        this.openMenuItem( alias );
      }, 450 ) );
    },
    mouseLeaveMenuItem () {
      this.clearTimeouts();
    },
    clearTimeouts () {
      this.timeouts.forEach( x => {
        window.clearTimeout( x );
      } );
      this.timeouts.length = 0;
    },
    openMenuItem ( alias ) {
      this.closeMenuItems();
      this.$set( this.menuItemsState, alias, true );
    },
    closeMenuItem ( alias ) {
      this.clearTimeouts();
      this.$set( this.menuItemsState, alias, false );
    },
    closeMenuItems () {
      Object.keys( this.menuItemsState )
        .filter( x => x !== "search" )
        .forEach( x => {
          this.$set( this.menuItemsState, x, false );
        } );
    },
    toggleMenuItem ( alias ) {
      this.$set( this.menuItemsState, alias, !this.menuItemsState[alias] );
    },
    openSearch () {
      const mutableState = { ...this.menuItemsState, search: true };
      this.menuItemsState = mutableState;
    },
    closeSearch () {
      const mutableState = { ...this.menuItemsState, search: false };
      this.menuItemsState = mutableState;
    },
    getMenuItemState ( alias ) {
      return this.menuItemsState[alias];
    },
    getMenuToggleClass ( alias ) {
      let result = {};

      if ( this.getMenuItemState( alias ) ) {
        result = {
          "main-navigation__accordion-toggle--open": true,
        };
      }

      return result;
    },
    getIconRotation ( alias ) {
      let result = null;

      if ( this.getMenuItemState( alias ) ) {
        result = 180;
      }

      return result;
    },
    closeOverlay () {
      if ( !this.preventHidingOverlay ) {
        this.showOverlay = false;
      }

      this.closeSearch();
    },
    traverseToNavigation ( column ) {
      const megaMenu = column.closest( ".mega-menu" );
      const megaMenuParent = megaMenu.closest( "li" );
      const { id: parentMenuId } = megaMenuParent;
      this.closeMenuItem( parentMenuId, false );

      const { firstChild } = megaMenuParent;
      if ( firstChild ) {
        firstChild.focus();
      }
    },
    traverseMegaMenuColumn ( column, keyCode, id ) {
      // action to be taken if down arrow is pressed within a megamenu column

      const menuItems = column.getElementsByClassName( "main-navigation__item" );
      if ( menuItems && menuItems.length > 0 ) {
        this.traverseElements( menuItems, keyCode, id );
      } else {
        const [firstLink] = column.getElementsByTagName( "a" );
        if ( firstLink ) {
          firstLink.focus();
        }
      }
    },
    traverseToMegaMenu () {
      const [megaMenu] = document.getElementsByClassName( "mega-menu" );
      const columns = megaMenu.getElementsByClassName( "main-navigation__column" );
      const [firstColumn] = Array.from( columns ).filter( col => col.id );

      if ( firstColumn ) {
        firstColumn.focus();
      }
    },
    traverseToSearch () {
      const [megaMenu] = document.getElementsByClassName( "mega-menu" );
      const [input] = megaMenu.getElementsByTagName( "input" );

      if ( input ) {
        input.focus();
      }
    },
    onNavKeyDown ( event ) {
      const focusedElement = document.activeElement;
      const { id } = focusedElement?.parentElement || { id: -1 };
      const { keyCode } = event;
      const {
        SPACE, UP, DOWN, LEFT, RIGHT,
      } = this.keyboardEvent.keyCode;

      // check for column ancestor indicating vertical controls
      const column = focusedElement.closest( ".main-navigation__column" );

      if ( column ) {
        // actions to be taken within mega menu
        switch ( keyCode ) {
          case UP: {
            if ( focusedElement === column ) {
              this.traverseToNavigation( column );
            } else if ( id ) {
              this.traverseMegaMenuColumn( column, keyCode, id );
            }
            event.preventDefault();
            break;
          }
          case DOWN: {
            this.traverseMegaMenuColumn( column, keyCode, id );
            event.preventDefault();
            break;
          }
          case LEFT:
          case RIGHT: {
            const { id: columnId } = column;
            if ( columnId ) {
              const columns = document.getElementsByClassName( "main-navigation__column" );
              // use only columns defined with an ID and tab-index within the DOM
              const elements = Array.from( columns ).filter( col => col.id );
              this.traverseElements( elements, keyCode, columnId, false );
            }
            event.preventDefault();
            break;
          }
          default:
            break;
        }
      } else {
        // actions to be taken within main navigation
        switch ( keyCode ) {
          case SPACE: {
            if ( id ) {
              this.toggleMenuItem( id );
              event.preventDefault();
            }
            break;
          }
          case UP: {
            if ( id ) {
              this.closeMenuItem( id, false );
              event.preventDefault();
            }
            break;
          }
          case DOWN: {
            if ( id ) {
              const isMegaMenuHidden = !this.menuItemsState[id];

              if ( isMegaMenuHidden ) {
                this.openMenuItem( id, true );
              } else if ( id === "search" ) {
                this.traverseToSearch();
              } else {
                this.traverseToMegaMenu();
              }

              event.preventDefault();
            }
            break;
          }
          case LEFT:
          case RIGHT: {
            if ( id ) {
              const menuItems = document.getElementsByClassName( "main-navigation__item" );
              this.traverseElements( menuItems, keyCode, id );
              event.preventDefault();
            }
            break;
          }
          default: {
            // console.info( keyCode );
          }
        }
      }
    },
    traverseElements ( elements, keyCode, currentElementId, targetFirstChild = true ) {
      const {
        UP, DOWN, LEFT, RIGHT,
      } = this.keyboardEvent.keyCode;

      // eslint-disable-next-line
      let tabIndex = Array.from(elements).findIndex(element => element.id === currentElementId);

      if ( keyCode === LEFT || keyCode === UP ) {
        tabIndex = tabIndex === 0 ? elements.length - 1 : tabIndex - 1;
      }
      if ( keyCode === RIGHT || keyCode === DOWN ) {
        tabIndex = tabIndex === elements.length - 1 ? 0 : tabIndex + 1;
      }

      const nextElement = elements[tabIndex];

      if ( targetFirstChild ) {
        // target anchor links
        const {
          childNodes: [nextChild],
        } = nextElement;

        nextChild.focus();
      } else {
        nextElement.focus();
      }
    },
  },
} );
